import { Modal, Spinner } from "react-bootstrap";
import './my-modal.styles.scss';
import CrossIcon from "../../icons/cross.icon";

/**
 * MyModal
 * @param {Object} [props]
 * @param {Boolean} [props.show]
 * @param {String} [props.className]
 * @param {String} [props.dialogClassName]
 * @param {ReactNode} [props.children]
 * @param {Function} [props.onHide]
 * @param {Object} [props.rest]
 * @returns {ReactNode}
 * @example
 *  <MyModal show>
      <MyModal.Header className="border-bottom" closeButton>
        <MyModal.HeaderTitle className="text-align-center w-100">
          Test
        </MyModal.HeaderTitle>
      </MyModal.Header>
      <MyModal.Body>
        Hello world
      </MyModal.Body>
      <MyModal.Footer className="d-flex gap-4 border-top">
        <MyModal.CancelButton>Cancel</MyModal.CancelButton>
        <MyModal.ApplyButton loading>Confirm</MyModal.ApplyButton>
      </MyModal.Footer>
    </MyModal>
 */
const MyModal = ({
  show,
  className = "",
  dialogClassName = "",
  children,
  onHide = () => { },
  ...rest
}) => {
  return (
    <Modal
      animation={false}
      show={show}
      dialogClassName={`my-modal-dialog ${dialogClassName}`}
      className={`my-modal ${className}`}
      centered
      scrollable
      onHide={onHide}
      {...rest}
    >
      {children}
    </Modal>
  );
}

/**
 * MyModal.Header
 * @param {Object} [props]
 * @param {String} [props.className]
 * @param {Boolean} [props.closeButton]
 * @param {Function} [props.onCrossClick]
 * @param {ReactNode} [props.children]
 * @returns {ReactNode}
 */
MyModal.Header = (props = {}) => (
  <Modal.Header {...props} className={`my-modal-header ${props.className ?? ''}`} closeButton={false}>
    {props.children}
    {props.closeButton && (
      <div className="d-flex align-items-center justify-content-center h-100">
        <CrossIcon onClick={props.onCrossClick} width="12px" className="pointer" fill="#616778" />
      </div>
    )}
  </Modal.Header>
);

/**
 * 
 * @param {Object} [props]  
 * @param {String} [props.className]
 * @param {ReactNode} [props.children]
 * @returns {ReactNode}
 */
MyModal.Body = (props = {}) => (
  <Modal.Body {...props} className={`my-modal-body ${props.className ?? ''}`}>
    {props.children}
  </Modal.Body>
);

/**
 * MyModal.Footer
 * @param {Object} [props]
 * @param {String} [props.className]
 * @param {ReactNode} [props.children]
 * @returns {ReactNode}
 */
MyModal.Footer = (props = {}) => (
  <Modal.Footer {...props} className={`my-modal-footer ${props.className ?? ''}`}>
    {props.children}
  </Modal.Footer>
);

/**
 * MyModal.HeaderTitle
 * @param {Object} [props]
 * @param {String} [props.className]
 * @param {ReactNode} [props.children]
 * @returns {ReactNode}
 */
MyModal.HeaderTitle = (props = {}) => (
  <h3 {...props} className={`m-0 f-14 line-height-22 f-black f-400 ${props.className ?? ''}`}>
    {props.children}
  </h3>
)

/**
 * MyModal.CancelButton
 * @param {Object} [props]
 * @param {String} [props.className]
 * @param {Function} [props.onClick]
 * @param {ReactNode} [props.children]  
 * @param {Boolean} [props.disabled]
 * @returns {ReactNode}
 */
MyModal.CancelButton = (props = {}) => (
  <button {...props} className={`my-modal-cancel-button ${props.className ?? ''} ${props.disabled ? 'disabled' : ''}`}>
    {props.children}
  </button>
)

/**
 * MyModal.ApplyButton
 * @param {Object} [props]
 * @param {String} [props.className]
 * @param {Boolean} [props.loading]
 * @param {Boolean} [props.red]
 * @param {Function} [props.onClick]
 * @param {ReactNode} [props.children]
 * @param {Boolean} [props.disabled]
 * @returns {ReactNode}
 */
MyModal.ApplyButton = (props = {}) => (
  <button 
    {...props} 
    className={`my-modal-apply-button d-flex gap-8 align-items-center ${props.className ?? ''} ${props.loading ? 'loading' : ''} ${props.red ? 'red' : ''}`}
  >
    {props.loading && (
      <Spinner
        animation="border"
        variant="secondary"
        size="sm"
        style={{
          width: "14px",
          height: "14px",
        }}
      />
    )}
    {props.children}
  </button>
)

export default MyModal;
